import React, { useEffect, useState } from "react";
import { useSiteContext } from "../SiteContext";
import { EmployeeTimeSheetWithDays, Supervisor, JobStatus, JobStatusEnum, PayrollApprovedMiscEmployeeTask } from "../interfaces/V2Interface";
import VistaBatchExportManager from "../managers/VistaBatchExportManager";
import { generateBatchTaskCSVForEmployees } from "../api/vistaBatchExport";

interface PopupProps {
  isModalVisible: boolean;
  closeModal: () => void;
  popupTitle: string;
  popupMessage: string;
}

interface VistaBatchExportModalProps {
  visible: boolean;
  onClose: () => void;
  supervisorName: string;
  supervisorId: string;
  supervisorAltId: string;
  startDate: string;
  endDate: string;
  createPopup: (title: string, message: string) => void;
}

// Loading component
const LoadingSpinner: React.FC<{ message: string }> = ({ message }) => (
  <div className="loading-container">
    <div className="loading-spinner"></div>
    <p className="loading-message">{message}</p>
  </div>
);

// Progress component for job status
const ProgressDisplay: React.FC<{ 
  jobStatus: JobStatus; 
  onShowDetails: () => void;
  translator: any;
}> = ({ jobStatus, onShowDetails, translator }) => (
  <div className="progress-container">
    <div className="progress-header">
      <h3>{translator.translate("ProcessingExport")}</h3>
      <div className={`job-status-badge job-status-${jobStatus.status.toLowerCase()}`}>
        {jobStatus.status}
      </div>
    </div>
    
    <div className="progress-bar-container">
      <div className="progress-bar">
        <div 
          className="progress-fill" 
          style={{ width: `${jobStatus.progress}%` }}
        ></div>
      </div>
      <span className="progress-text">{jobStatus.progress}%</span>
    </div>
    
    <p className="current-step">{jobStatus.currentStep}</p>
    
    {jobStatus.status === JobStatusEnum.FAILED && (
      <div className="error-actions">
        <button 
          className="details-button" 
          onClick={onShowDetails}
        >
          {translator.translate("MoreDetails")}
        </button>
      </div>
    )}
  </div>
);

const VistaBatchExportModal: React.FC<VistaBatchExportModalProps> = ({
  visible,
  onClose,
  supervisorName,
  supervisorId,
  supervisorAltId,
  startDate,
  endDate,
  createPopup,
}) => {
  const { payPeriodID, payPeriods, translator } = useSiteContext();
  const [employees, setEmployees] = useState<EmployeeTimeSheetWithDays[]>([]);
  const [selectedEmployees, setSelectedEmployees] = useState<Set<string>>(new Set());
  const [employeeIDsWithUnapprovedMisc, setEmployeeIDsWithUnapprovedMisc] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState(true);
  const [batchData, setBatchData] = useState<{ batchId: number; keyID: number; totalEmployees: number; totalEntries: number; totalPieces: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [fetchingEmployees, setFetchingEmployees] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [jobStatus, setJobStatus] = useState<JobStatus | null>(null);
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const [errorDetails, setErrorDetails] = useState<any>(null);

  // Find the current pay period to get the week number
  const currentPayPeriod = payPeriods.find(pp => pp.PayPeriodID === payPeriodID);
  const weekNumber = currentPayPeriod?.WeekNumber || 0;

  // Helper function to format employee name
  const formatName = (name: { first: string; middle?: string; last: string } | string) => {
    if (typeof name === 'string') return name;
    return `${name.first} ${name.middle ? name.middle + ' ' : ''}${name.last}`;
  };

  // True if the employee has any red (misc task) nobody has acted on yet.
  //
  // THE TEST IS "IS SuperDescription BLANK", AND ONLY THAT. A blank description means
  // no approver has touched the red; anything written there means one has, whichever
  // workflow they used:
  //   - V1 misc-task page  — approve writes SuperDescription, leaves ApprovalStatus alone
  //   - two-level (TAS-360) — the level-2 approver is the one who writes SuperDescription
  //   - deny               — copies the deny reason in, and a denied red is terminal and
  //                          zeroed, so there is nothing left for anyone to wait on
  // So the one test answers correctly for all three, today and after NPM finishes moving
  // to the two-level flow.
  //
  // DO NOT REINTRODUCE AN ApprovalStatus CHECK. It was here (`status !== approved &&
  // status !== denied`) and it over-blocked badly: NPM runs two-level approval ON while
  // its approvers are still working the V1 page, so a normally-approved red today is
  // {ApprovalStatus: 1|2, SuperDescription: "..."}. The API exports that red — see
  // API/src/riceServices/timecards/timecardProcessor.ts, which ships a red on status 3
  // OR on not-denied-with-a-description — but this gate called it pending and locked the
  // whole employee out of the export.
  //
  // Known gap, deliberate: a force-approved red (OverrideMiscTaskApproval, status 3, no
  // description) blocks here where the API would export. Rare, and blocking is the safe
  // direction.
  //
  // KEEP THIS IN LOCKSTEP WITH VistaBatchExportV2Modal.tsx. The two modals are
  // mounted side by side from SupervisorListPage and must agree on which employees
  // are exportable; a change to one and not the other is a silent split.
  const isMiscTaskPendingApproval = (misc: PayrollApprovedMiscEmployeeTask): boolean =>
    ((misc.superDescription ?? '') as string).trim() === '';

  const hasUnapprovedMiscTask = (emp: EmployeeTimeSheetWithDays): boolean => {
    const days = emp.days ?? [];
    for (const day of days) {
      const projectTasks = day.projectEmployeeTasks ?? [];
      for (const pt of projectTasks) {
        const miscTasks = pt.miscEmployeeTasks ?? [];
        for (const misc of miscTasks) {
          if (isMiscTaskPendingApproval(misc)) return true;
        }
      }
    }
    return false;
  };

  // Reset modal state
  const resetModal = () => {
    setBatchData(null);
    setSelectedEmployees(new Set());
    setEmployeeIDsWithUnapprovedMisc(new Set());
    setSelectAll(true);
    setError(null);
    setFetchError(null);
    setEmployees([]);
    setFetchingEmployees(true);
    setLoading(false);
    setJobStatus(null);
    setShowErrorDetails(false);
    setErrorDetails(null);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const exportableEmployeeIDs = employees
    .filter((emp) => !employeeIDsWithUnapprovedMisc.has((emp.employeeID ?? '').toString()))
    .map((emp) => (emp.employeeID ?? '').toString());
  const exportableCount = exportableEmployeeIDs.length;

  const handleSelectAllChange = () => {
    if (selectAll) {
      setSelectedEmployees(new Set());
    } else {
      setSelectedEmployees(new Set(exportableEmployeeIDs));
    }
    setSelectAll(!selectAll);
  };

  const handleCheckboxChange = (employeeID: string) => {
    setSelectedEmployees((prev) => {
      const updated = new Set(prev);
      updated.has(employeeID) ? updated.delete(employeeID) : updated.add(employeeID);
      setSelectAll(exportableCount > 0 && updated.size === exportableCount);
      return updated;
    });
  };

  type ExportJobStarter = (supervisorID: number, payPeriodID: number, employeeIDs: string[]) => Promise<number>;

  const runBatchExport = async (startExportJob: ExportJobStarter) => {
    if (selectedEmployees.size === 0) {
      setError(translator.translate("PleaseSelectEmployee"));
      createPopup(translator.translate("ExportError"), translator.translate("PleaseSelectEmployee"));
      return;
    }

    setLoading(true);
    setError(null);
    setJobStatus(null);
    setErrorDetails(null);

    try {
      const employeeIDs = employees
        .filter(emp => selectedEmployees.has((emp.employeeID ?? '').toString()) && !employeeIDsWithUnapprovedMisc.has((emp.employeeID ?? '').toString()))
        .map(emp => (emp.employeeID ?? '').toString());

      const jobID = await startExportJob(parseInt(supervisorId), payPeriodID, employeeIDs);

      const result = await VistaBatchExportManager.pollJobStatus(
        jobID,
        (status) => setJobStatus(status)
      );

      if ('error' in result) {
        setErrorDetails(result.error);
        const errorMessagePopup = result.error?.errorDetails
          ? `${translator.translate("ExportFailed")}\n\n${JSON.stringify(result.error.errorDetails, null, 2)}`
          : translator.translate("ExportFailed");
        setError(translator.translate("ExportFailed"));
        createPopup(translator.translate("ExportError"), errorMessagePopup);
      } else {
        setBatchData(result);
      }
    } catch (error) {
      console.error("Error in Vista batch export:", error);
      const errorMessage = error instanceof Error ? error.message : translator.translate("FailedCompleteBatchExport");
      setError(errorMessage);
      createPopup(translator.translate("ExportError"), translator.translate("FailedCompleteBatchExport"));
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmExport = () => runBatchExport(VistaBatchExportManager.generateVistaBatchExport.bind(VistaBatchExportManager));
  const handleExportTimeOnly = () => runBatchExport(VistaBatchExportManager.generateVistaBatchExportTimeOnly.bind(VistaBatchExportManager));
  const handleExportPieceworksOnly = () => runBatchExport(VistaBatchExportManager.generateVistaBatchExportPieceworksOnly.bind(VistaBatchExportManager));

  const handleGeneratePiecesCSV = async () => {
    if (selectedEmployees.size === 0) {
      setError(translator.translate("PleaseSelectEmployee"));
      createPopup(translator.translate("ExportError"), translator.translate("PleaseSelectEmployee"));
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const employeeIDs = employees
        .filter(emp => selectedEmployees.has((emp.employeeID ?? '').toString()) && !employeeIDsWithUnapprovedMisc.has((emp.employeeID ?? '').toString()))
        .map(emp => (emp.employeeID ?? '').toString());

      const year = currentPayPeriod?.AccountingYear || new Date().getFullYear();

      await generateBatchTaskCSVForEmployees(
        parseInt(supervisorId),
        payPeriodID,
        employeeIDs,
        year,
        weekNumber,
        supervisorAltId
      );
    } catch (error) {
      console.error("Error generating Pieces CSV:", error);
      const errorMessage = error instanceof Error ? error.message : translator.translate("FailedCompleteBatchExport");
      setError(errorMessage);
      createPopup(translator.translate("ExportError"), errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleShowErrorDetails = () => {
    setShowErrorDetails(true);
  };

  useEffect(() => {
    if (visible && supervisorId && payPeriodID) {
      const fetchEmployees = async () => {
        try {
          setFetchError(null);
          setFetchingEmployees(true);
          
          const employeesData = await VistaBatchExportManager.getEmployees(
            supervisorId,
            payPeriodID
          );

          employeesData.sort((a: EmployeeTimeSheetWithDays, b: EmployeeTimeSheetWithDays) => {
            const altA = parseInt(a.altEmployeeID || '0', 10) || 0;
            const altB = parseInt(b.altEmployeeID || '0', 10) || 0;
            return altA - altB;
          })
          
          if (!employeesData || employeesData.length === 0) {
            throw new Error(translator.translate("NoEmployeesFoundSupervisor"));
          }
          
          setEmployees(employeesData);

          const unapprovedSet = new Set<string>();
          employeesData.forEach((emp: EmployeeTimeSheetWithDays) => {
            if (hasUnapprovedMiscTask(emp)) unapprovedSet.add((emp.employeeID ?? '').toString());
          });
          setEmployeeIDsWithUnapprovedMisc(unapprovedSet);

          const exportableIDs = employeesData
            .filter((emp: EmployeeTimeSheetWithDays) => !unapprovedSet.has((emp.employeeID ?? '').toString()))
            .map((emp: EmployeeTimeSheetWithDays) => (emp.employeeID ?? '').toString());
          setSelectedEmployees(new Set(exportableIDs));
          setSelectAll(exportableIDs.length > 0);
          
        } catch (err) {
          console.error("Failed to fetch data:", err);
          const errorMessage = err instanceof Error ? err.message : translator.translate("FailedFetchEmployeeData");
          setFetchError(errorMessage);
          createPopup(translator.translate("FetchError"), translator.translate("FailedFetchEmployeeData"));
          setEmployees([]);
          setSelectedEmployees(new Set());
        } finally {
          setFetchingEmployees(false);
        }
      };

      fetchEmployees();
    }
}, [visible, supervisorId, payPeriodID, startDate, endDate, translator]);

  if (!visible) return null;

  return (
    <div className="batch-export-modal-overlay">
      <div className="batch-export-modal" onClick={(e) => e.stopPropagation()}>
        {fetchingEmployees ? (
          <LoadingSpinner message={translator.translate("LoadingEmployees")} />
        ) : loading && jobStatus ? (
          <ProgressDisplay 
            jobStatus={jobStatus} 
            onShowDetails={handleShowErrorDetails}
            translator={translator}
          />
        ) : loading ? (
          <LoadingSpinner message={translator.translate("StartingExport")} />
        ) : showErrorDetails ? (
          <div className="error-details-container">
            <h3>{translator.translate("ErrorDetails")}</h3>
            <pre className="error-details-content">
              {JSON.stringify(errorDetails, null, 2)}
            </pre>
            <button 
              className="batch-export-button" 
              onClick={() => setShowErrorDetails(false)}
            >
              {translator.translate("Back")}
            </button>
          </div>
        ) : (
          <>
            <h2 className="batch-export-title">
              Vista Export for {supervisorName}
            </h2>

            <div className="batch-export-content">
              <p>{translator.translate("SupervisorID")} {supervisorAltId}</p>
              <p>{translator.translate("Week")} {weekNumber}</p>
              
              {/* Show fetch error if it exists */}
              {fetchError && (
                <div className="batch-export-error">
                  <p>{translator.translate("ErrorLoadingEmployees")} {fetchError}</p>
                </div>
              )}
              
              {/* Show export error if it exists */}
              {error && !fetchError && <p className="batch-export-error">{error}</p>}

              {employeeIDsWithUnapprovedMisc.size > 0 && (
                <p className="batch-export-unapproved-misc-message">
                  {/* `translator.translate` returns undefined for a key that is not in
                      strings.json, which is why this literal was the only copy anyone
                      ever saw. The key now EXISTS (en + es), so the literal is a real
                      fallback again rather than the de-facto text. */}
                  {employeeIDsWithUnapprovedMisc.size} {translator.translate("UnapprovedMiscTaskMessage") || "employee(s) have a miscellaneous task still awaiting approval and cannot be exported."}
                </p>
              )}
            </div>

            {!batchData ? (
              <>
                {employees.length > 0 && (
                  <>
                    <div className="batch-export-select-all">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAllChange}
                        id="batch-export-select-all"
                        disabled={loading || employees.length === 0 || exportableCount === 0}
                      />
                      <label htmlFor="batch-export-select-all">
                        {translator.translate("SelectAll")} ({selectedEmployees.size} of {exportableCount} {translator.translate("Selected")})
                      </label>
                    </div>

                    <div className="batch-export-employee-list-container">
                      <ul className="batch-export-employee-list">
                        {employees.map((emp) => {
                          const empIdStr = (emp.employeeID ?? '').toString();
                          const isUnapprovedMisc = employeeIDsWithUnapprovedMisc.has(empIdStr);
                          return (
                            <li
                              key={empIdStr}
                              className={`batch-export-employee-item ${isUnapprovedMisc ? 'batch-export-employee-item-unapproved-misc' : ''}`}
                            >
                              {isUnapprovedMisc ? (
                                <span>
                                  {formatName(emp.name)} ({translator.translate("EmployeeID")} {emp.altEmployeeID})
                                </span>
                              ) : (
                                <label>
                                  <input
                                    type="checkbox"
                                    checked={selectedEmployees.has(empIdStr)}
                                    onChange={() => handleCheckboxChange(empIdStr)}
                                    disabled={loading}
                                  />
                                  {formatName(emp.name)} ({translator.translate("EmployeeID")} {emp.altEmployeeID})
                                </label>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  </>
                )}

                <div className="batch-export-modal-footer">
                  {employees.length > 0 && (
                    <>
                      <button 
                        className="batch-export-button" 
                        onClick={handleConfirmExport}
                        disabled={loading || selectedEmployees.size === 0}
                      >
                        Export Time and Pieces ({selectedEmployees.size})
                      </button>
                      <button 
                        className="batch-export-button" 
                        onClick={handleExportTimeOnly}
                        disabled={loading || selectedEmployees.size === 0}
                      >
                        Export Time Only ({selectedEmployees.size})
                      </button>
                      <button 
                        className="batch-export-button" 
                        onClick={handleExportPieceworksOnly}
                        disabled={loading || selectedEmployees.size === 0}
                      >
                        Export Pieces Only ({selectedEmployees.size})
                      </button>
                      <button 
                        className="batch-export-button" 
                        onClick={handleGeneratePiecesCSV}
                        disabled={loading || selectedEmployees.size === 0}
                      >
                        Generate Pieces CSV ({selectedEmployees.size})
                      </button>
                    </>
                  )}
                  <button 
                    className="batch-export-cancel" 
                    onClick={handleClose}
                    disabled={loading}
                  >
                    {employees.length === 0 ? translator.translate("Close") : translator.translate("Cancel")}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="batch-export-success">
                  <p>Vista Export Successful!</p>
                  <div className="batch-export-batch-ids">
                    <div className="batch-export-field">
                      <label>Batch ID</label>
                      <input
                        type="text"
                        value={batchData.batchId}
                        readOnly
                        className="batch-export-textbox"
                      />
                    </div>
                    <div className="batch-export-field">
                      <label>Key ID</label>
                      <input
                        type="text"
                        value={batchData.keyID}
                        readOnly
                        className="batch-export-textbox"
                      />
                    </div>
                    <div className="batch-export-field">
                      <label>Total Employees</label>
                      <input
                        type="text"
                        value={batchData.totalEmployees}
                        readOnly
                        className="batch-export-textbox"
                      />
                    </div>
                    <div className="batch-export-field">
                      <label>Total Time Entries</label>
                      <input
                        type="text"
                        value={batchData.totalEntries}
                        readOnly
                        className="batch-export-textbox"
                      />
                    </div>
                    <div className="batch-export-field">
                      <label>Total Pieces</label>
                      <input
                        type="text"
                        value={batchData.totalPieces}
                        readOnly
                        className="batch-export-textbox"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="batch-export-modal-footer">
                  <button 
                    className="batch-export-button" 
                    onClick={handleClose}
                  >
                    {translator.translate("Close")}
                  </button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const styles = `
.batch-export-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
}

.batch-export-modal {
  background: #fff;
  padding: 30px;
  border-radius: 10px;
  width: 500px;
  max-width: 90%;
  max-height: 80vh;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  position: relative;
  display: flex;
  flex-direction: column;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 15px;
}

.loading-message {
  color: #666;
  font-size: 16px;
  margin: 0;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.batch-export-success {
  margin: 20px 0;
  padding: 15px;
  background-color: #f8fff8;
  border: 1px solid #e0e0e0;
  border-radius: 5px;
}

.batch-export-batch-ids {
  margin-top: 15px;
}

.batch-export-field {
  margin-bottom: 10px;
}

.batch-export-field label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
}

.batch-export-textbox {
  width: 100%;
  padding: 8px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #f9f9f9;
}

.batch-export-error {
  color: #d32f2f;
  background-color: #fde8e8;
  padding: 10px;
  border-radius: 4px;
  margin: 10px 0;
}

.batch-export-unapproved-misc-message {
  color: #b71c1c;
  background-color: #ffebee;
  padding: 8px 10px;
  border-radius: 4px;
  margin: 10px 0;
  font-size: 14px;
}

.batch-export-employee-item-unapproved-misc {
  background-color: #ffebee;
}

.batch-export-retry {
  margin-top: 8px;
  padding: 6px 12px;
  background-color: #1976d2;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.batch-export-retry:hover {
  background-color: #1565c0;
}

.progress-container {
  padding: 20px;
  text-align: center;
}

.progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.progress-header h3 {
  margin: 0;
  color: #333;
}

.job-status-badge {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
}

.job-status-pending {
  background-color: #fff3cd;
  color: #856404;
}

.job-status-processing {
  background-color: #d1ecf1;
  color: #0c5460;
}

.job-status-completed {
  background-color: #d4edda;
  color: #155724;
}

.job-status-failed {
  background-color: #f8d7da;
  color: #721c24;
}

.progress-bar-container {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
}

.progress-bar {
  flex: 1;
  height: 20px;
  background-color: #e0e0e0;
  border-radius: 10px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background-color: #4caf50;
  transition: width 0.3s ease;
}

.progress-text {
  font-weight: bold;
  color: #333;
  min-width: 40px;
}

.current-step {
  color: #666;
  font-size: 14px;
  margin: 0;
}

.error-actions {
  margin-top: 15px;
}

.details-button {
  padding: 8px 16px;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.details-button:hover {
  background-color: #c82333;
}

.error-details-container {
  padding: 20px;
}

.error-details-container h3 {
  margin-top: 0;
  color: #dc3545;
}

.error-details-content {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  padding: 15px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 300px;
  overflow-y: auto;
  margin: 15px 0;
}

.batch-export-modal-footer {
  display: flex;
  justify-content: flex-start;
  gap: 12px;
  margin-top: 20px;
  flex-wrap: wrap;
}

.batch-export-button {
  padding: 10px 20px;
  background-color: #1976d2;
  color: white;
  border: none;
  border-radius: 4px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.batch-export-button:hover:not(:disabled) {
  background-color: #1565c0;
}

.batch-export-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.batch-export-cancel {
  padding: 10px 20px;
  background-color: #ccc;
  color: black;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.batch-export-cancel:hover:not(:disabled) {
  background-color: #999;
}

.batch-export-cancel:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
`;

const styleTag = document.createElement('style');
styleTag.innerHTML = styles;
document.head.appendChild(styleTag);

export default VistaBatchExportModal; 